import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import { formatDate } from '../utils/dateFormat';

const writeRoles = ['superadmin', 'admin', 'whitelabel_admin', 'employee'];
const paymentOptions = [
  { value: 'cash', label: 'Cash' },
  { value: 'credit', label: 'Credit' },
  { value: 'online', label: 'Online' },
  { value: 'upi', label: 'UPI' },
  { value: 'gpay', label: 'GPay' },
  { value: 'paytm', label: 'Paytm' },
  { value: 'phonepe', label: 'PhonePe' },
];

const accountOptions = [
  { value: 'customer', label: 'Customer Payment' },
  { value: 'dealer', label: 'Dealer Payment' },
  { value: 'vendor', label: 'Purchase/Vendor Payment' },
];

const accountLabels = {
  customer: 'Customer',
  dealer: 'Dealer',
  vendor: 'Purchase/Vendor',
  purchase: 'Purchase/Vendor',
  admin: 'Admin',
  whitelabel_admin: 'White Label Admin',
  employee: 'Employee'
};

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
};

const money = (value) => `Rs. ${Number(value || 0).toFixed(2)}`;
const modeLabel = (mode) => paymentOptions.find((item) => item.value === mode)?.label || mode || '-';
const accountLabel = (type) => accountLabels[type] || accountOptions.find((item) => item.value === type)?.label || type || '-';

export default function Payments({ paymentView = '' }) {
  const user = getStoredUser();
  const canRecordPayment = writeRoles.includes(user?.role);
  const canViewPurchaseDetails = ['superadmin', 'admin', 'whitelabel_admin'].includes(user?.role);
  const visibleAccountOptions = accountOptions.filter((item) => canViewPurchaseDetails || item.value !== 'vendor');
  const [payments, setPayments] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [activeTab, setActiveTab] = useState('payments');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [ledgerFilter, setLedgerFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [fromType, setFromType] = useState('customer');
  const [fromId, setFromId] = useState('');
  const [purchaseId, setPurchaseId] = useState('');
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState('cash');
  const [upiRef, setUpiRef] = useState('');
  const [note, setNote] = useState('');
  const [customers, setCustomers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [purchases, setPurchases] = useState([]);

  const getHeaders = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);

  const fetchAll = useCallback(async () => {
    try {
      const [payRes, ledgerRes] = await Promise.all([
        axios.get(`${API_URL}/api/payments`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/payments/ledger`, { headers: getHeaders() }),
      ]);
      setPayments(payRes.data || []);
      setLedger(ledgerRes.data || []);

      if (canRecordPayment) {
        const [custRes, usersRes, vendorRes, purchaseRes] = await Promise.all([
          axios.get(`${API_URL}/api/customers`, { headers: getHeaders() }),
          axios.get(`${API_URL}/api/users`, { headers: getHeaders() }),
          canViewPurchaseDetails ? axios.get(`${API_URL}/api/purchases/vendors`, { headers: getHeaders() }) : Promise.resolve({ data: [] }),
          canViewPurchaseDetails ? axios.get(`${API_URL}/api/purchases`, { headers: getHeaders() }) : Promise.resolve({ data: [] }),
        ]);
        setCustomers(custRes.data || []);
        setDealers((usersRes.data || []).filter(u => u.role === 'dealer'));
        setVendors(vendorRes.data || []);
        setPurchases(purchaseRes.data || []);
      }
    } catch (err) {
      console.log(err);
    }
  }, [canRecordPayment, canViewPurchaseDetails, getHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const resetForm = () => {
    setFromId('');
    setPurchaseId('');
    setAmount('');
    setUpiRef('');
    setNote('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canRecordPayment) return;
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/payments`, {
        from_type: fromType,
        from_id: fromId,
        purchase_id: fromType === 'vendor' ? (purchaseId || null) : null,
        amount,
        payment_mode: mode,
        upi_ref: upiRef,
        note
      }, { headers: getHeaders() });
      alert('Payment recorded successfully!');
      setShowForm(false);
      resetForm();
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const inp = { width: '100%', padding: '10px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
  const lbl = { display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 'bold', color: '#374151' };
  const todayKey = new Date().toDateString();
  const sumPayments = (predicate) => payments.reduce((sum, p) => predicate(p) ? sum + Number(p.amount || 0) : sum, 0);
  const totalPaid = sumPayments(() => true);
  const todayPaid = sumPayments((p) => p.created_at && new Date(p.created_at).toDateString() === todayKey);
  const customerPaid = sumPayments((p) => p.from_type === 'customer');
  const dealerPaid = sumPayments((p) => p.from_type === 'dealer');
  const vendorPaid = canViewPurchaseDetails ? sumPayments((p) => p.from_type === 'vendor' || p.from_type === 'purchase') : 0;
  const modeSum = (modes) => sumPayments((p) => modes.includes(p.payment_mode));
  const modeColors = {
    cash: { bg: '#ecfdf5', color: '#047857' },
    credit: { bg: '#fff7ed', color: '#c2410c' },
    online: { bg: '#e0f2fe', color: '#0369a1' },
    upi: { bg: '#ede9fe', color: '#7c3aed' },
    gpay: { bg: '#eff6ff', color: '#1d4ed8' },
    paytm: { bg: '#eef2ff', color: '#4338ca' },
    phonepe: { bg: '#f5f3ff', color: '#6d28d9' },
  };
  const typeColors = {
    customer: { bg: '#d1fae5', color: '#065f46' },
    dealer: { bg: '#dbeafe', color: '#1d4ed8' },
    vendor: { bg: '#fef3c7', color: '#92400e' },
    purchase: { bg: '#fef3c7', color: '#92400e' },
  };
  const needsReference = ['online', 'upi', 'gpay', 'paytm', 'phonepe'].includes(mode);
  const accountList = fromType === 'customer' ? customers : fromType === 'dealer' ? dealers : vendors;
  const vendorPurchases = purchases.filter((purchase) => String(purchase.vendor_id) === String(fromId));
  const visiblePayments = paymentFilter === 'all'
    ? payments.filter((payment) => canViewPurchaseDetails || (payment.from_type !== 'vendor' && payment.from_type !== 'purchase'))
    : payments.filter((payment) => payment.from_type === paymentFilter || (paymentFilter === 'vendor' && payment.from_type === 'purchase'));
  const visibleLedger = ledgerFilter === 'all'
    ? ledger.filter((row) => canViewPurchaseDetails || row.account_type !== 'vendor')
    : ledger.filter((row) => row.account_type === ledgerFilter);
  const ledgerAccountOptions = [
    { value: 'customer', label: 'Customer Credit/Debit' },
    { value: 'dealer', label: 'Dealer Credit/Debit' },
    { value: 'admin', label: 'Admin Credit/Debit' },
    { value: 'whitelabel_admin', label: 'White Label Admin Credit/Debit' },
    { value: 'employee', label: 'Employee Credit/Debit' },
    ...(canViewPurchaseDetails ? [{ value: 'vendor', label: 'Purchase/Vendor Credit/Debit' }] : []),
  ];

  useEffect(() => {
    if (!paymentView) return;
    const recordType = {
      'payments-record-customer': 'customer',
      'payments-record-dealer': 'dealer',
      'payments-record-vendor': 'vendor'
    }[paymentView];
    if (paymentView === 'payments-record' || recordType) {
      setActiveTab('payments');
      setShowForm(true);
      if (recordType) setFromType(recordType);
      return;
    }
    const paymentFilterMap = {
      'payments-history-all': 'all',
      'payments-history-customer': 'customer',
      'payments-history-dealer': 'dealer',
      'payments-history-vendor': 'vendor'
    };
    if (paymentFilterMap[paymentView]) {
      setShowForm(false);
      setActiveTab('payments');
      setPaymentFilter(paymentFilterMap[paymentView]);
      return;
    }
    const ledgerFilterMap = {
      'payments-ledger-all': 'all',
      'payments-ledger-admin': 'admin',
      'payments-ledger-employee': 'employee',
      'payments-ledger-dealer': 'dealer',
      'payments-ledger-vendor': 'vendor'
    };
    if (ledgerFilterMap[paymentView]) {
      setShowForm(false);
      setActiveTab('ledger');
      setLedgerFilter(ledgerFilterMap[paymentView]);
    }
  }, [paymentView]);

  return (
    <div className="fixed-panel-page">
      <div className="fixed-panel-header">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '20px' }}>
        <div>
          <h2 style={{ color: '#1e3a5f', margin: 0 }}>Payments</h2>
          {!canRecordPayment && (
            <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '13px' }}>
              View-only access for payments and ledger records linked to your account.
            </p>
          )}
        </div>
        {canRecordPayment && (
          <button onClick={() => setShowForm(!showForm)} style={{ background: '#1e3a5f', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>
            {showForm ? 'Cancel' : '+ Record Payment'}
          </button>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px', marginBottom: '18px' }}>
        {[
          { label: 'Total Collected', value: money(totalPaid), color: '#10b981' },
          { label: "Today's Payment", value: money(todayPaid), color: '#06b6d4' },
          { label: 'Customer Payment', value: money(customerPaid), color: '#047857' },
          { label: 'Dealer Payment', value: money(dealerPaid), color: '#2563eb' },
          ...(canViewPurchaseDetails ? [{ label: 'Purchase/Vendor Payment', value: money(vendorPaid), color: '#f97316' }] : []),
          { label: 'Credit Mode', value: money(modeSum(['credit'])), color: '#8b5cf6' },
        ].map(card => (
          <div key={card.label} style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', borderTop: `4px solid ${card.color}` }}>
            <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#6b7280' }}>{card.label}</p>
            <p style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: card.color }}>{card.value}</p>
          </div>
        ))}
      </div>
      </div>

      <div className="fixed-panel-body">
      {canRecordPayment && showForm && (
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h3 style={{ color: '#1e3a5f', marginTop: 0 }}>Record New Payment</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' }}>
              <div>
                <label style={lbl}>Payment From</label>
                <select style={inp} value={fromType} onChange={e => { setFromType(e.target.value); resetForm(); }}>
                  {visibleAccountOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Select {fromType === 'vendor' ? 'Vendor/Purchaser' : fromType === 'customer' ? 'Customer' : 'Dealer'} *</label>
                <select style={inp} value={fromId} onChange={e => { setFromId(e.target.value); setPurchaseId(''); }} required>
                  <option value="">Select...</option>
                  {accountList.map(item => (
                    <option key={item.id} value={item.id}>
                      {item.name} {item.mobile ? `- ${item.mobile}` : ''} {fromType === 'vendor' ? `- Balance ${money(item.balance)}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              {fromType === 'vendor' && (
                <div>
                  <label style={lbl}>Purchase Invoice</label>
                  <select style={inp} value={purchaseId} onChange={e => setPurchaseId(e.target.value)}>
                    <option value="">Auto adjust oldest pending purchase</option>
                    {vendorPurchases.map((purchase) => {
                      const balance = Number(purchase.total_amount || 0) - Number(purchase.paid_amount || 0);
                      return (
                        <option key={purchase.id} value={purchase.id}>
                          {purchase.invoice_no || `Purchase #${purchase.id}`} - {purchase.product_name || 'Item'} - Balance {money(balance)}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}
              <div><label style={lbl}>Amount *</label><input type="number" style={inp} value={amount} onChange={e => setAmount(e.target.value)} placeholder="Amount" required /></div>
              <div>
                <label style={lbl}>Payment Mode</label>
                <select style={inp} value={mode} onChange={e => setMode(e.target.value)}>
                  {paymentOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                </select>
              </div>
              {needsReference && <div><label style={lbl}>Transaction Reference</label><input style={inp} value={upiRef} onChange={e => setUpiRef(e.target.value)} placeholder="Transaction ID or reference" /></div>}
              <div><label style={lbl}>Note</label><input style={inp} value={note} onChange={e => setNote(e.target.value)} placeholder="Any notes..." /></div>
            </div>
            <button type="submit" disabled={loading} style={{ marginTop: '16px', background: '#10b981', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', fontSize: '15px', cursor: 'pointer', fontWeight: 'bold' }}>
              {loading ? 'Saving...' : 'Save Payment'}
            </button>
          </form>
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {[{ key: 'payments', label: 'Payment History' }, { key: 'ledger', label: 'Credit/Debit Ledger' }].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{ padding: '8px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: activeTab === tab.key ? '#1e3a5f' : 'white', color: activeTab === tab.key ? 'white' : '#374151', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'payments' && (
        <>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
            {[{ value: 'all', label: 'All Payments' }, ...visibleAccountOptions].map((item) => (
              <button key={item.value} type="button" onClick={() => setPaymentFilter(item.value)} style={{ padding: '7px 13px', borderRadius: '999px', border: '1px solid #dbe3ef', cursor: 'pointer', background: paymentFilter === item.value ? '#1e3a5f' : 'white', color: paymentFilter === item.value ? 'white' : '#1e3a5f', fontSize: '13px', fontWeight: 'bold' }}>
                {item.label}
              </button>
            ))}
          </div>
          <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: '1160px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#1e3a5f', color: 'white' }}>
                  {['#', 'From', 'Mobile', 'Vehicle', 'Invoice', 'Type', 'Amount', 'Mode', 'Reference', 'Note', 'Recorded By', 'Date'].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visiblePayments.length === 0 ? (
                  <tr><td colSpan="12" style={{ padding: '24px', textAlign: 'center', color: '#666' }}>No payments found</td></tr>
                ) : visiblePayments.map((p, i) => {
                  const modeStyle = modeColors[p.payment_mode] || { bg: '#f3f4f6', color: '#374151' };
                  const typeStyle = typeColors[p.from_type] || { bg: '#f3f4f6', color: '#374151' };
                  return (
                    <tr key={p.id} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? 'white' : '#f9fafb' }}>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{i + 1}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold' }}>{p.from_name || `ID: ${p.from_id}`}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{p.from_mobile || '-'}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{p.vehicle_no || '-'}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{p.invoice_no || '-'}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                        <span style={{ background: typeStyle.bg, color: typeStyle.color, padding: '3px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' }}>{accountLabel(p.from_type)}</span>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold', color: '#10b981' }}>{money(p.amount)}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>
                        <span style={{ background: modeStyle.bg, color: modeStyle.color, padding: '3px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' }}>{modeLabel(p.payment_mode)}</span>
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{p.upi_ref || '-'}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{p.note || '-'}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{p.recorded_by_name || '-'}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{p.created_at ? formatDate(p.created_at) : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeTab === 'ledger' && (
        <>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
            {[
              { value: 'all', label: 'All Credit/Debit' },
              ...ledgerAccountOptions,
            ].map((item) => (
              <button key={item.value} type="button" onClick={() => setLedgerFilter(item.value)} style={{ padding: '7px 13px', borderRadius: '999px', border: '1px solid #dbe3ef', cursor: 'pointer', background: ledgerFilter === item.value ? '#1e3a5f' : 'white', color: ledgerFilter === item.value ? 'white' : '#1e3a5f', fontSize: '13px', fontWeight: 'bold' }}>
                {item.label}
              </button>
            ))}
          </div>
          <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
            <table style={{ width: '100%', minWidth: '1280px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#1e3a5f', color: 'white' }}>
                  {['#', 'Account', 'Type', 'Mobile', 'Vehicle', 'Debit', 'Credit', 'Balance', 'Cash', 'Credit Mode', 'Online', 'GPay', 'Paytm', 'PhonePe', 'Last Payment'].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: '13px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleLedger.length === 0 ? (
                  <tr><td colSpan="15" style={{ padding: '24px', textAlign: 'center', color: '#666' }}>No ledger records found</td></tr>
                ) : visibleLedger.map((l, i) => {
                  const debit = Number(l.total_debit ?? l.total_purchase ?? 0);
                  const credit = Number(l.total_credit ?? l.total_paid ?? 0);
                  const balance = Number(l.pending ?? (debit - credit));
                  return (
                    <tr key={`${l.account_type || 'account'}-${l.id}`} style={{ borderBottom: '1px solid #f3f4f6', background: i % 2 === 0 ? 'white' : '#f9fafb' }}>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{i + 1}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: 'bold' }}>{l.account_name || l.dealer_name || '-'}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{accountLabel(l.account_type || 'dealer')}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{l.account_mobile || l.dealer_mobile || '-'}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{l.vehicle_no || '-'}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{money(debit)}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px', color: '#10b981', fontWeight: 'bold' }}>{money(credit)}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}><span style={{ color: balance > 0 ? '#dc2626' : '#10b981', fontWeight: 'bold' }}>{money(balance)}</span></td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{money(l.cash_amount)}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{money(l.credit_amount)}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{money(l.online_amount)}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{money(l.gpay_amount)}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{money(l.paytm_amount)}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{money(l.phonepe_amount)}</td>
                      <td style={{ padding: '12px 16px', fontSize: '13px' }}>{l.last_payment_date ? formatDate(l.last_payment_date) : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
      </div>
    </div>
  );
}
